import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button } from '~/components/Button';
import { showToast } from '~/components/Toast';
import { Toggle } from '~/components/Toggle';
import { DetailPageError } from '~/modules/errors/components/DetailPageError';
import { useLabel } from '~/modules/settings/api/queries/label';
import { CTABlock } from '~/modules/settings/components/CTABlock';
import { SETTINGS_ROUTES } from '~/modules/settings/config/routes';
import type { LabelDetailTransformed } from '~/modules/settings/utils/transformers';
import { transformLabelDetails } from '~/modules/settings/utils/transformers';
import type { LabelDetail } from '~settings/api/lib/schemas';
import { useLabelEdit } from '~settings/api/mutations/labelEdit';
import type { LabelFormSchema } from '~settings/modules/label/components/LabelForm';
import { LabelForm } from '~settings/modules/label/components/LabelForm';
import {
  getErrorMessage,
  getLabelPayload,
  getLabelValues,
} from '~settings/modules/label/helpers';

import { EditLabelHeader } from './components/EditLabelHeader';

const FORM_ID = 'editLabelForm';

export function EditLabelPage() {
  const { t } = useTranslation('settings');
  const { labelId = '' } = useParams();
  const navigate = useNavigate();

  const { isLoading, error, label } = useLabel<LabelDetailTransformed>(
    labelId,
    {
      select: transformLabelDetails,
    },
  );

  const { mutate: editLabel, isPending } = useLabelEdit(labelId, {
    onSuccess: (data: LabelDetail) => {
      navigate(SETTINGS_ROUTES.LABEL_DETAIL(data.id));
    },
    onError: async (err) => {
      const message = await getErrorMessage(err, t);

      showToast(message);
    },
  });

  const [isActive, setIsActive] = useState<boolean>(label?.active ?? true);
  const labelValues = useMemo<LabelFormSchema | null>(
    () => (label ? getLabelValues(label) : null),
    [label],
  );

  if (isLoading) {
    return <section className="py-md">{t('loading')}</section>;
  }

  if (error || !label || !labelValues || !labelId) {
    return (
      <DetailPageError
        errorCode={error?.response?.status}
        fallbackRoute={{
          href: SETTINGS_ROUTES.LABEL_LIST,
          label: t('backToUserManagement'),
        }}
      />
    );
  }

  const onSubmit = (data: LabelFormSchema) => {
    const payload = getLabelPayload(data, isActive);

    editLabel(payload);
  };

  return (
    <article className="relative flex min-h-full flex-col">
      <EditLabelHeader
        data={label}
        actions={
          <Toggle
            pressed={isActive}
            onPressedChange={setIsActive}
            activeLabel={t('active')}
            inactiveLabel={t('inactive')}
          />
        }
      />
      <div className="grow overflow-y-auto pb-xl">
        <LabelForm
          onSubmit={onSubmit}
          values={labelValues}
          isActive={isActive}
          id={FORM_ID}
        />
      </div>

      <CTABlock>
        <Button asChild variant="secondary" size="large">
          <Link to={SETTINGS_ROUTES.LABEL_DETAIL(labelId)}>
            {t('buttons.cancel')}
          </Link>
        </Button>
        <Button
          variant="primary"
          type="submit"
          form={FORM_ID}
          size="large"
          disabled={isPending}
        >
          {t('buttons.saveChanges')}
        </Button>
      </CTABlock>
    </article>
  );
}

EditLabelPage.displayName = 'EditLabelPage';
